import { useTranslation } from "react-i18next";
import { memo, useCallback, useMemo, useState } from "react";
import { ChevronRight, Plus, X } from "lucide-react";
import type { FacilityNode, FacilityPermissionLevel, RoleGrant } from "@arc/contracts";
import type { FacilityTreeNode } from "../facilities/facility-tree.js";
import { FACILITY_PERMISSION_LEVELS } from "@arc/contracts";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { facilityNodeIcon } from "@/components/icons";
import { PERMISSION_LEVEL_META, PermissionLevelBadge, permissionRank } from "@/components/permission-level";
import { cn } from "@/lib/utils";
import { buildForest, excludeArchived, flattenVisible, searchTree, toggle } from "../facilities/facility-tree.js";
import { partitionGrants } from "./role-groups.js";

/**
 * A role's facility grants over the caller's ACCESS-MANAGE-scoped tree (ADR-030 §3.4, read-only in Phase A). Node
 * states, each carried by a glyph or text (never colour alone): granted (the effective-level badge; a grant above the
 * role's cap says so), grants below (a ringed dot + text — DISTINCT from granted, the legacy styled them identically),
 * not administered by the caller (dimmed + text). Archived facilities are excluded and counted; grants on facilities
 * outside the caller's tree are counted. WAI-ARIA tree semantics with a read-only keyboard subset.
 *
 * MEMOISED (with the rows below): the editor re-renders on every filter keystroke (the urgent pass keeps the OLD
 * deferred query), and with stable props this whole tree skips that pass — only the deferred pass re-filters.
 */
export const GrantTree = memo(function GrantTree({
  flat,
  grants,
  childGrantIds,
  bypass,
  rootFacilityId = null,
  onSetRoot,
  hpfp = null,
  canEdit = false,
  pendingFacilityId = null,
  onGrant,
  onRevoke,
  query = "",
}: {
  flat: readonly FacilityNode[];
  grants: readonly RoleGrant[];
  childGrantIds: readonly number[];
  bypass: boolean;
  /** The role's base folder, marked on its row (Phase B). */
  rootFacilityId?: number | null;
  /** Supers only (Phase B): the explicit "Set as base folder" row action — the legacy hid this behind an invisible hotspot. */
  onSetRoot?: (facilityId: number) => void;
  /** Phase C — the live grants. The level select is bounded by the role's HPFP; a capped grant stays read-only. */
  hpfp?: FacilityPermissionLevel | null;
  canEdit?: boolean;
  pendingFacilityId?: number | null;
  onGrant?: (facilityId: number, level: FacilityPermissionLevel, applyToChildren: boolean) => void;
  onRevoke?: (facilityId: number, applyToChildren: boolean) => void;
  /** A name filter (owner): only the matching facilities and their ancestors render, the ancestors forced open. */
  query?: string;
}) {
  const { t } = useTranslation();
  // Per-row "sub-facilities too" switch state (the legacy asked yes/no per action; here it is a visible choice).
  const [applyBelow, setApplyBelow] = useState<ReadonlySet<number>>(new Set());
  const offered = useMemo(() => (hpfp ? FACILITY_PERMISSION_LEVELS.filter((l) => permissionRank(l) <= permissionRank(hpfp)) : []), [hpfp]);
  const active = useMemo(() => excludeArchived(flat), [flat]);
  const forest = useMemo(() => buildForest(active), [active]);
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(() => new Set(active.filter((f) => f.parentId === null).map((f) => f.id)));
  const search = useMemo(() => searchTree(active, query), [active, query]);
  const filtering = query.trim() !== "";
  const rows = useMemo(
    () => flattenVisible(forest, { isExpanded: (id) => expanded.has(id) || search.expandedIds.has(id), isVisible: (id) => search.visibleIds.has(id) }),
    [forest, expanded, search],
  );
  const { visible, outsideCount, archivedCount } = useMemo(
    () => partitionGrants(grants, new Set(active.map((f) => f.id)), new Set(flat.filter((f) => f.archived).map((f) => f.id))),
    [grants, active, flat],
  );
  const below = useMemo(() => new Set(childGrantIds), [childGrantIds]);
  const toggleExpand = useCallback((id: number) => setExpanded((prev) => toggle(prev, id)), []);
  const toggleBelow = useCallback((id: number) => setApplyBelow((prev) => toggle(prev, id)), []);

  // An EDITABLE role with no grants yet still needs the tree — that is where its first grant is made (Phase C).
  const live = canEdit && !!onGrant && !!onRevoke && offered.length > 0;
  if (grants.length === 0 && childGrantIds.length === 0 && !onSetRoot && !live) {
    return <p className="text-muted-foreground text-sm">{t("roles.noFacilityGrantsYet")}</p>;
  }
  if (filtering && rows.length === 0) {
    return <p className="text-muted-foreground text-sm">{t("roles.noFacilityMatches", { query: query.trim() })}</p>;
  }
  return (
    <div className="flex flex-col gap-2">
      <ul role="tree" aria-label={t("roles.facilityGrants")} className="m-0 list-none p-0">
        {rows.map(({ node, depth, expanded: isExpanded, hasChildren }) => {
          const grant = visible.get(node.id);
          const administered = bypass || node.permission === "access_manage";
          const capped = !!grant && grant.level !== grant.effectiveLevel;
          return (
            <GrantRow
              key={node.id}
              node={node}
              depth={depth}
              isExpanded={isExpanded}
              hasChildren={hasChildren}
              grant={grant}
              hasBelow={!grant && below.has(node.id)}
              administered={administered}
              rowLive={live && administered && !capped}
              capped={capped}
              isPending={pendingFacilityId === node.id}
              armed={applyBelow.has(node.id)}
              isRoot={rootFacilityId === node.id}
              offered={offered}
              onToggleExpand={toggleExpand}
              onToggleBelow={toggleBelow}
              onSetRoot={onSetRoot}
              onGrant={onGrant}
              onRevoke={onRevoke}
            />
          );
        })}
      </ul>
      {outsideCount > 0 || archivedCount > 0 ? (
        <p className="text-muted-foreground text-xs">
          {[
            outsideCount > 0 ? t("roles.onFacilitiesNotAdministered", { count: outsideCount }) : null,
            archivedCount > 0 ? t("roles.onArchivedFacilities", { count: archivedCount }) : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
      ) : null}
    </div>
  );
});

const NONE = "__none";

interface GrantRowProps {
  node: FacilityTreeNode;
  depth: number;
  isExpanded: boolean;
  /** Children in the FILTERED view (drives the expand chevron only). */
  hasChildren: boolean;
  grant: RoleGrant | undefined;
  hasBelow: boolean;
  administered: boolean;
  rowLive: boolean;
  capped: boolean;
  isPending: boolean;
  /** "Sub-facilities too" armed on this row. */
  armed: boolean;
  isRoot: boolean;
  offered: readonly FacilityPermissionLevel[];
  onToggleExpand: (id: number) => void;
  onToggleBelow: (id: number) => void;
  onSetRoot?: (facilityId: number) => void;
  onGrant?: (facilityId: number, level: FacilityPermissionLevel, applyToChildren: boolean) => void;
  onRevoke?: (facilityId: number, applyToChildren: boolean) => void;
}

/**
 * One facility row — MEMOISED with primitive props and stable callbacks, so typing in the name filter (or toggling one
 * row) re-renders only the rows whose inputs moved, not the few hundred react-aria selects of the whole tree (owner:
 * the filter lagged per keystroke).
 */
const GrantRow = memo(function GrantRow({ node, depth, isExpanded, hasChildren, grant, hasBelow, administered, rowLive, capped, isPending, armed, isRoot, offered, onToggleExpand, onToggleBelow, onSetRoot, onGrant, onRevoke }: GrantRowProps) {
  const { t } = useTranslation();
          // The cascade controls follow the FULL tree, never the filtered view: a filter that hides a parent's children
          // must not hide an armed "Sub-facilities too" while the select still cascades (panel, High). `hasChildren`
          // (the filtered view) only drives the expand chevron.
          const hasChildrenInTree = node.children.length > 0;
          const cascadeArmed = hasChildrenInTree && armed;
          const Icon = facilityNodeIcon(node);
          return (
            <li
              key={node.id}
              role="treeitem"
              aria-level={depth + 1}
              aria-expanded={hasChildren ? isExpanded : undefined}
              aria-label={node.name}
              className={cn("flex items-stretch gap-1", !administered && "opacity-60")}
              style={{ paddingLeft: `calc(${depth} * 1.5rem)` }}
            >
              <div aria-hidden="true" className="relative flex w-5 shrink-0 items-center justify-center self-stretch">
                <span className="bg-border absolute inset-y-0 left-1/2 w-px -translate-x-1/2" />
                <span className={cn("bg-background relative z-10 size-2.5 rounded-full border-2", hasBelow ? "border-primary" : grant ? "border-primary bg-primary" : "border-border")} />
              </div>
              <div className="group border-border bg-background my-0.5 flex min-h-10 flex-1 items-center gap-2 rounded-lg border px-3 py-1">
                {hasChildren ? (
                  <button
                    type="button"
                    aria-label={isExpanded ? t("roles.collapseName", { name: node.name }) : t("roles.expandName", { name: node.name })}
                    onClick={() => onToggleExpand(node.id)}
                    className="text-muted-foreground hover:text-foreground -ml-1 flex size-5 shrink-0 items-center justify-center rounded border-0 bg-transparent"
                  >
                    <ChevronRight className={cn("size-4 transition-transform", isExpanded && "rotate-90")} />
                  </button>
                ) : (
                  <span className="size-5 shrink-0" />
                )}
                <Icon aria-hidden="true" className="text-muted-foreground size-4 shrink-0" />
                <span className="min-w-0 flex-1 truncate text-sm">{node.name}</span>
                {!administered ? <span className="sr-only">{t("roles.youDontAdministerThisFacility")}</span> : null}
                {isRoot ? (
                  <span className="bg-muted text-muted-foreground rounded-full px-2 py-0.5 text-[10px] font-medium uppercase">{t("roles.baseFolder")}</span>
                ) : onSetRoot ? (
                  <button
                    type="button"
                    onClick={() => onSetRoot(node.id)}
                    // Revealed on hover / keyboard focus only: a text action on every row would drown the tree (live check).
                    className="text-muted-foreground hover:text-foreground focus-visible:opacity-100 rounded border-0 bg-transparent px-1 text-xs underline-offset-2 opacity-0 group-hover:opacity-100 hover:underline"
                  >
                    {t("roles.setAsBaseFolder")}
                  </button>
                ) : null}
                {hasBelow ? (
                  <span role="img" aria-label={t("roles.grantsBelow")} className="text-muted-foreground text-xs">
                    {t("roles.grantsBelow")}
                  </span>
                ) : null}
                {rowLive ? (
                  <span className="flex items-center gap-2">
                    {hasChildrenInTree ? (
                      <Switch isSelected={armed} onChange={() => onToggleBelow(node.id)} isDisabled={isPending} aria-label={t("roles.subFacilitiesTooName", { name: node.name })} className="text-xs">
                        {t("roles.subFacilitiesToo")}
                      </Switch>
                    ) : null}
                    {cascadeArmed ? (
                      // The cascade of what the node ALREADY holds: react-aria's Select fires nothing for an unchanged
                      // pick, so pushing the current level down (or clearing below an ungranted parent) is this
                      // explicit action, revealed by the switch (panel consensus).
                      <Button
                        variant="outline"
                        size="sm"
                        isDisabled={isPending}
                        aria-label={
                          grant
                            ? t("roles.applyLevelBelow", { label: t(PERMISSION_LEVEL_META[grant.effectiveLevel].labelKey), name: node.name })
                            : t("roles.removeGrantsBelow", { name: node.name })
                        }
                        onPress={() => (grant ? onGrant?.(node.id, grant.effectiveLevel, true) : onRevoke?.(node.id, true))}
                        className="h-7 text-xs"
                      >
                        {grant ? t("roles.applyBelow") : t("roles.clearBelow")}
                      </Button>
                    ) : null}
                    <Select
                      className="text-xs"
                      placeholder={t("roles.noGrant")}
                      isDisabled={isPending}
                      selectedKey={grant ? grant.effectiveLevel : NONE}
                      aria-label={t("roles.levelForName", { name: node.name })}
                      onSelectionChange={(key) => {
                        if (key == null) return;
                        // With "Sub-facilities too" ON the pick is an APPLY action: re-choosing the level the node
                        // already holds pushes it DOWN the tree, and "No grant" on an ungranted parent revokes below
                        // (panel consensus — an equality guard silently swallowed the cascade). With it OFF an
                        // unchanged pick is a no-op.
                        const below = cascadeArmed;
                        const current = grant?.effectiveLevel ?? NONE;
                        if (key === NONE) {
                          if (grant || below) onRevoke?.(node.id, below);
                        } else if (below || key !== current) onGrant?.(node.id, key as FacilityPermissionLevel, below);
                      }}
                    >
                      {/* The legacy's glyph button: the trigger shows only the CURRENT level's icon (a "+" when there is no
                          grant), named for assistive tech and tooltipped for everyone else; the list carries icon + label. */}
                      <TooltipTrigger delay={0} focusable={false}>
                        <SelectTrigger
                          chevron={false}
                          className="size-8 justify-center px-0"
                          renderValue={() => {
                            const CurrentIcon = grant ? PERMISSION_LEVEL_META[grant.effectiveLevel].Icon : Plus;
                            return <CurrentIcon role="img" aria-label={grant ? t(PERMISSION_LEVEL_META[grant.effectiveLevel].labelKey) : t("roles.noGrant")} className="size-4" />;
                          }}
                        />
                        <Tooltip placement="left">{grant ? t("roles.levelChangeHint", { label: t(PERMISSION_LEVEL_META[grant.effectiveLevel].labelKey) }) : t("roles.noGrantChooseALevel")}</Tooltip>
                      </TooltipTrigger>
                      {/* The legacy's icon BAR: one glyph per option in a row (× = no grant, then the levels up to the cap); the
                          label of the highlighted glyph appears beneath it — a long label ("Manage facilities") never has to
                          fit a cell. Each option's accessible name IS that label (always in the DOM, faded when idle). */}
                      <SelectContent orientation="horizontal" popoverClassName="w-auto min-w-0" className="relative flex min-w-28 flex-row items-center gap-0.5 pb-5">
                        {[{ id: NONE, label: t("roles.noGrant"), Icon: X }, ...offered.map((l) => ({ id: l, label: t(PERMISSION_LEVEL_META[l].labelKey), Icon: PERMISSION_LEVEL_META[l].Icon }))].map(({ id, label, Icon: OptionIcon }) => (
                          <SelectItem key={id} id={id} textValue={label} indicator="none" className="static size-9 justify-center px-0">
                            {({ isFocused, isHovered }) => (
                              <>
                                <OptionIcon aria-hidden="true" className="size-4" />
                                {/* ONE caption slot spanning the whole bar (anchored to the list, not the 36px cell), so the
                                    widest label never clips at the popover's edge (panel). */}
                                <span
                                  className={cn(
                                    "text-popover-foreground pointer-events-none absolute inset-x-0 bottom-0 text-center text-[11px] leading-none whitespace-nowrap transition-opacity",
                                    isFocused || isHovered ? "opacity-100" : "opacity-0",
                                  )}
                                >
                                  {label}
                                </span>
                              </>
                            )}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </span>
                ) : grant ? (
                  capped ? (
                    <TooltipTrigger delay={0}>
                      <span className="inline-flex" tabIndex={0}>
                        <PermissionLevelBadge level={grant.effectiveLevel} prefix={t("roles.grantedPrefix")} />
                      </span>
                      <Tooltip placement="left">
                        {t("roles.grantedCapped", {
                          level: t(PERMISSION_LEVEL_META[grant.level].labelKey),
                          capped: t(PERMISSION_LEVEL_META[grant.effectiveLevel].labelKey),
                        })}
                      </Tooltip>
                    </TooltipTrigger>
                  ) : (
                    <PermissionLevelBadge level={grant.effectiveLevel} prefix={t("roles.grantedPrefix")} />
                  )
                ) : null}
              </div>
            </li>
          );
});
